import _, {clamp, compact, flatten, identity, isUndefined, times} from 'lodash';
import React, {
  FC,
  PropsWithChildren,
  ReactElement,
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import styled, {css} from 'styled-components';

import {FrontWindow, globalWindow} from '../../../../libs/shared/helpers/src/browser/browserHelpers';
import {getWindow} from '../../../../libs/shared/helpers/src/browser/domHelpers';
import {TaskSourcesEnum} from '../../../../libs/shared/helpers/src/instrumentation/eventLoopPerformanceHelpers';
import {useInitialEffect} from '../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {useForceUpdate} from '../../../../libs/shared/helpers/src/react/hooks/useForceUpdate';
import {
  EventHandler,
  FrontEvent,
  HandlerOf,
  PredicateOf,
  RendererOf,
} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {isDefined} from '../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {useSize} from '../../../../libs/shared/ui/src/hooks/sizeHooks';
import {ScrollDirectionsEnum, ScrollerProps} from '../../../../srcCommon/scroll/scrollerTypes';
import {AsyncMoreStatusesEnum} from '../../../store/appTypes';

/*
 * Constants.
 */

export const scrollContainerTestId = 'virtual-scroller--scroll-container';

/*
 * Props.
 */

export type VirtualScrollerProps<TId> = ScrollerProps<TId> & {
  className?: string;
  /**
   * Allows the customizing of the inner width of the virtual scroller.
   *   If your row elements contain sticky properties, you can't rely on width 100% since that will only
   *   fill the size of the parent and the sticky will stop when you hit the parents width.
   */
  width?: number;

  onScroll?: HandlerOf<VirtualScrollerScrollContext>;

  // If false, the scroller will not attempt to account for changes in the item
  // list (such as items getting added at the top) when computing the new scroll
  // position. Useful for static scenarios where we are changing items manually.
  //
  // Defaults to true.
  shouldComputeLayoutDelta?: boolean;
};

export interface VirtualScrollerScrollContext {
  event: FrontEvent;
  scrollTop: number;
  scrollLeft: number;
  scrollRemainingSpaceRight: number;
}

interface SectionProps {
  devicePixelRatio: number;
  sectionTop: number;
  sectionHeight: number;
  sectionIndex: number;
  renderSectionHeader: (sectionIndex: number, topOffset: number) => ReactNode;
}

interface ItemProps<TId> {
  devicePixelRatio: number;
  itemId: TId | undefined;
  itemTop: number;
  itemHeight: number;
  renderItem: RendererOf<TId>;
  validateItem: PredicateOf<TId>;
}

/*
 * Style.
 */

const StyledOuterDiv = styled.div`
  position: relative;
  height: 100%;
`;

const StyledInnerDiv = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  overflow: auto;
  /* Prevents the macOS overlay scrollbar from appearing behind the items on Safari/Chrome/Electron. */
  will-change: transform;
`;

interface StyledListDivProps {
  $width?: number;
}

const StyledListDiv = styled.div<StyledListDivProps>`
  position: absolute;
  width: 100%;

  ${(p) =>
    p.$width &&
    css`
      width: ${p.$width}px;
    `};
`;

const StyledSectionHeaderDiv = styled.div`
  position: absolute;
  width: 100%;
  /* Give each item its own compositing layer, so only the changed/moved items need to be repainted. */
  will-change: transform;
`;

const StyledItemDiv = styled.div`
  position: absolute;
  width: 100%;
  /* Give each item its own compositing layer, so only the changed/moved items need to be repainted. */
  will-change: transform;
`;

const StyledLoaderDiv = styled.div`
  position: absolute;
  width: 100%;
`;

/*
 * Component.
 */

interface VirtualScrollerLayout<TId> {
  /** Partially or fully visible items. */
  visibleItemIds: ReadonlyArray<TId>;
  /** Focused item, regardless of visibility. */
  focusedId?: TId;
  /** Virtual position of the first partial or fully visible item. */
  firstVisibleItemTop: number;
  itemHeight: number;
}

const initialViewportLayout: VirtualScrollerLayout<any> = {
  visibleItemIds: [],
  firstVisibleItemTop: 0,
  itemHeight: 0,
};

export function Item<TId>(props: ItemProps<TId>) {
  const {devicePixelRatio, itemHeight, itemId, itemTop, renderItem, validateItem} = props;
  const children = useItemChildren(itemId, renderItem, validateItem);

  return useMemo(() => {
    const top = roundToNearestDevicePixel(itemTop, devicePixelRatio);

    return (
      <StyledItemDiv
        style={{
          display: isUndefined(itemId) ? 'none' : 'initial',
          transform: `translateY(${top}px)`,
          height: itemHeight,
        }}
      >
        {children}
      </StyledItemDiv>
    );
  }, [children, devicePixelRatio, itemHeight, itemId, itemTop]);
}

export const Section: FC<PropsWithChildren<SectionProps>> = (props) => {
  const {children, devicePixelRatio, sectionHeight, sectionIndex, sectionTop, renderSectionHeader} = props;

  const top = roundToNearestDevicePixel(sectionTop, devicePixelRatio);
  const header = useSectionHeaderChildren(sectionIndex, top, renderSectionHeader);

  return useMemo(
    () => (
      <StyledSectionHeaderDiv
        style={{
          display:
            (children && Array.isArray(children) && children.length > 0) || top < sectionHeight
              ? 'initial'
              : 'none',
          transform: `translateY(${top}px)`,
          height: sectionHeight,
        }}
      >
        {header}
        {children}
      </StyledSectionHeaderDiv>
    ),
    [children, header, sectionHeight, top],
  );
};

export const VirtualScroller = <TId,>(
  props: VirtualScrollerProps<TId>,
): ReturnType<FC<PropsWithChildren<unknown>>> => {
  /*
   * Virtual layout.
   */

  const {
    itemIds,
    itemHeight,
    moreStatuses,
    loaderHeight,
    padding = {},
    width,
    onScroll: customOnScroll,
    shouldComputeLayoutDelta = true,
  } = props;

  const {sectionHeaderHeight, renderSectionHeader} =
    'renderSectionHeader' in props ? props : {sectionHeaderHeight: undefined, renderSectionHeader: undefined};

  const flatItemIds = useMemo(() => flatten(itemIds), [itemIds]);

  // Compute the layout of the top loader.
  const moreStatusTop = moreStatuses[ScrollDirectionsEnum.TOP];
  const hasLoaderTop = computeHasLoader(moreStatusTop);
  const loaderTopHeight = hasLoaderTop ? loaderHeight : 0;
  const loaderTopTop = 0;
  const loaderTopBottom = loaderTopTop + loaderTopHeight;

  const paddingTop = padding.top || 0;
  const paddingBottom = padding.bottom || 0;

  // Compute the layout of the items.
  const itemIdsHeight: ReadonlyArray<number> = useMemo(
    () =>
      itemIds.map((itemId: TId | ReadonlyArray<TId>) => {
        // If itemIds is a 2D array, we should add sectionHeaderHeight.
        if (Array.isArray(itemId) && sectionHeaderHeight) {
          return itemId.length * itemHeight + sectionHeaderHeight;
        }

        return itemHeight;
      }),
    [itemHeight, itemIds, sectionHeaderHeight],
  );

  const allItemsHeight = itemIdsHeight.reduce((totalHeight, currentHeight) => totalHeight + currentHeight, 0);
  const firstItemTop = loaderTopBottom + paddingTop;
  const lastItemBottom = firstItemTop + allItemsHeight;

  // Compute the layout of the bottom loader.
  const moreStatusBottom = moreStatuses[ScrollDirectionsEnum.BOTTOM];
  const hasLoaderBottom = computeHasLoader(moreStatusBottom);
  const loaderBottomHeight = hasLoaderBottom ? loaderHeight : 0;
  const loaderBottomTop = lastItemBottom;
  const loaderBottomBottom = loaderBottomTop + loaderBottomHeight;

  // Compute our virtual height.
  const virtualHeight = loaderBottomBottom + paddingBottom;

  /*
   * Viewport layout.
   */

  // Measure the size of our viewport.
  const outerRef = useRef<HTMLDivElement>(null);
  const viewportSize = useSize(outerRef);
  const viewportHeight = viewportSize ? viewportSize.height : 0;

  // Auto-scroll to the focused item only once we have a reasonable viewport height (and after we have measured it)
  // and once it gets added to our items.
  const [focusedId, focusedItemTop] = findItem(
    flatItemIds,
    viewportHeight >= itemHeight ? props.focusedId : undefined,
    firstItemTop,
    itemHeight,
  );

  // Compute layout changes since the last render.
  const prevLayoutRef = useRef<VirtualScrollerLayout<TId>>(initialViewportLayout);
  const prevLayout = prevLayoutRef.current;
  const layoutDelta = shouldComputeLayoutDelta
    ? computeVirtualScrollerLayoutDelta<TId>(prevLayout, flatItemIds, firstItemTop, itemHeight)
    : 0;

  // Compute our legal scroll range.
  const [minScrollTop, maxScrollTop, absoluteMaxScrollTop] = computeVirtualScrollerScrollRange(
    prevLayout,
    focusedId,
    focusedItemTop,
    itemHeight,
    virtualHeight,
    viewportHeight,
  );

  // Compute our scroll position.
  const innerRef = useRef<HTMLDivElement>(null);
  const [scrollTop, onScroll] = useScroll(
    innerRef,
    absoluteMaxScrollTop,
    (nextScrollTop) => {
      const shouldStayAtTop = computeShouldStayAtTop(
        prevLayout,
        firstItemTop,
        itemHeight,
        viewportHeight,
        layoutDelta,
        nextScrollTop,
      );

      return computeNextScrollTop(
        shouldStayAtTop ? minScrollTop : nextScrollTop + layoutDelta,
        minScrollTop,
        maxScrollTop,
        viewportHeight,
      );
    },
    customOnScroll,
  );
  const scrollBottom = Math.max(0, virtualHeight - viewportHeight - scrollTop);

  // Compute the index of the first visible item.
  const viewportStartIndex = Math.floor(
    Math.max(0, scrollTop - firstItemTop - (sectionHeaderHeight || 0)) / itemHeight,
  );
  // Make sure the number of items is stable for any given viewport height, so that we can optimize
  // scrolling by "rotating" the same reusable items.
  const viewportItemCount = Math.ceil(viewportHeight / itemHeight) + 1;

  const viewportItemIds = useMemo(
    () => flatItemIds.slice(viewportStartIndex, viewportStartIndex + viewportItemCount),
    [flatItemIds, viewportStartIndex, viewportItemCount],
  );

  // Take a snapshot of our layout for the next render.
  prevLayoutRef.current = {
    visibleItemIds: viewportItemIds,
    focusedId,
    firstVisibleItemTop: firstItemTop + viewportStartIndex * itemHeight,
    itemHeight,
  };

  // Measure our device pixel ratio. Should update at the same time as our viewport size.
  const {devicePixelRatio} = getWindow(outerRef.current) || globalWindow;

  /*
   * Item allocation.
   */

  const viewportItemIndexes = useMemoryViewportItemIndexes(viewportItemIds);

  /*
   * Effects.
   */

  const loadMoreThreshold = Math.min(viewportHeight, Math.max(loaderHeight, viewportHeight * 0.4));
  const shouldLoadMoreTop = scrollTop < loadMoreThreshold && moreStatusTop === AsyncMoreStatusesEnum.HAS_MORE;
  const shouldLoadMoreBottom =
    scrollBottom < loadMoreThreshold && moreStatusBottom === AsyncMoreStatusesEnum.HAS_MORE;

  useEffect(() => {
    if (shouldLoadMoreTop) {
      props.onLoadMore(ScrollDirectionsEnum.TOP);
    }

    if (shouldLoadMoreBottom) {
      props.onLoadMore(ScrollDirectionsEnum.BOTTOM);
    }
  });

  /*
   * Render.
   */

  const {renderItem, renderLoader} = props;

  const renderedLoaderTop = useMemo(
    () =>
      hasLoaderTop ? (
        <StyledLoaderDiv style={{transform: `translateY(${loaderTopTop}px)`, height: loaderHeight}}>
          {renderLoader({
            direction: ScrollDirectionsEnum.TOP,
            moreStatus: moreStatusTop,
          })}
        </StyledLoaderDiv>
      ) : null,
    [hasLoaderTop, loaderTopTop, loaderHeight, renderLoader, moreStatusTop],
  );

  const renderedLoaderBottom = useMemo(
    () =>
      hasLoaderBottom ? (
        <StyledLoaderDiv style={{transform: `translateY(${loaderBottomTop}px)`, height: loaderHeight}}>
          {renderLoader({
            direction: ScrollDirectionsEnum.BOTTOM,
            moreStatus: moreStatusBottom,
          })}
        </StyledLoaderDiv>
      ) : null,
    [hasLoaderBottom, loaderBottomTop, loaderHeight, renderLoader, moreStatusBottom],
  );

  // Called by reusable items to determine if their content can be kept alive despite being off-screen.
  const validateItem = useCallback<PredicateOf<TId>>((id) => flatItemIds.includes(id), [flatItemIds]);

  const children = useMemo(() => {
    if (sectionHeaderHeight && renderSectionHeader) {
      // @ts-expect-error https://github.com/microsoft/TypeScript/issues/33591
      return itemIds.reduce(
        (acc: ReactElement[], sectionItemIds: ReadonlyArray<TId>, sectionIndex: number) => {
          const renderedItems = viewportItemIndexes.map((viewportItemIndex, reusableItemIndex) => {
            // Depending on how our viewport lines up, we may have more items that we need.
            const itemId =
              viewportItemIndex >= 0 && viewportItemIndex < viewportItemIds.length
                ? viewportItemIds[viewportItemIndex]
                : undefined;

            if (!itemId || !sectionItemIds.includes(itemId)) {
              return null;
            }

            const itemIndex = sectionItemIds.indexOf(itemId);
            return (
              <Item
                // eslint-disable-next-line react/no-array-index-key
                key={reusableItemIndex}
                devicePixelRatio={devicePixelRatio}
                itemId={itemId}
                itemTop={itemIndex * itemHeight}
                itemHeight={itemHeight}
                renderItem={renderItem}
                validateItem={validateItem}
              />
            );
          });
          const compactedRenderedItems = compact(renderedItems);

          const sectionTop = (acc.length > 0 ? itemIdsHeight[sectionIndex - 1] : 0) + loaderTopHeight;
          return acc.concat(
            <Section
              // eslint-disable-next-line react/no-array-index-key
              key={sectionIndex}
              devicePixelRatio={devicePixelRatio}
              renderSectionHeader={renderSectionHeader}
              sectionHeight={sectionItemIds.length * itemHeight + sectionHeaderHeight}
              sectionIndex={sectionIndex}
              sectionTop={sectionTop}
            >
              {compactedRenderedItems}
            </Section>,
          );
        },
        [],
      );
    }

    return viewportItemIndexes.map((viewportItemIndex, reusableItemIndex) => {
      // Depending on how our viewport lines up, we may have more items that we need.
      const itemId =
        viewportItemIndex >= 0 && viewportItemIndex < viewportItemIds.length
          ? viewportItemIds[viewportItemIndex]
          : undefined;

      const itemIndex = viewportStartIndex + viewportItemIndex;
      return (
        <Item
          // eslint-disable-next-line react/no-array-index-key
          key={reusableItemIndex}
          devicePixelRatio={devicePixelRatio}
          itemId={itemId}
          itemTop={firstItemTop + itemIndex * itemHeight}
          itemHeight={itemHeight}
          renderItem={renderItem}
          validateItem={validateItem}
        />
      );
    });
  }, [
    devicePixelRatio,
    firstItemTop,
    itemHeight,
    itemIds,
    itemIdsHeight,
    loaderTopHeight,
    renderItem,
    renderSectionHeader,
    sectionHeaderHeight,
    validateItem,
    viewportItemIds,
    viewportItemIndexes,
    viewportStartIndex,
  ]);

  return (
    <StyledOuterDiv className={props.className} ref={outerRef}>
      <StyledInnerDiv onScroll={onScroll} ref={innerRef} data-testid={scrollContainerTestId}>
        <StyledListDiv $width={width} style={{height: virtualHeight}}>
          {renderedLoaderTop}
          {children}
          {renderedLoaderBottom}
        </StyledListDiv>
      </StyledInnerDiv>
    </StyledOuterDiv>
  );
};

/*
 * Hooks.
 */

/** Returns a re-entrant, auto-canceling, and window-aware version of requestAnimationFrame. */
function useAnimationFrame(elementRef: RefObject<Element>): (callback: FrameRequestCallback) => void {
  const windowRef = useRef<FrontWindow>();
  const handleRef = useRef<number>();

  // Cancel any pending animation frames when we unmount.
  useInitialEffect(() => () => {
    const handle = handleRef.current;
    if (isUndefined(handle)) {
      return;
    }

    const window = windowRef.current;
    if (!window) {
      return;
    }

    window.cancelAnimationFrame(handle);
  });

  return useCallback(
    (callback) => {
      // Do we already have a pending animation frame?
      const handle = handleRef.current;
      if (!isUndefined(handle)) {
        return;
      }

      // Find a window.
      const window = windowRef.current || getWindow(elementRef.current);
      if (!window) {
        return;
      }

      // Request the animation frame, and save the window + handle for canceling later.
      windowRef.current = window;
      handleRef.current = window.requestInstrumentedAnimationFrame(
        (time: number) => {
          // Prepare for the next animation frame request.
          handleRef.current = undefined;

          callback(time);
        },
        {source: TaskSourcesEnum.COMPONENT_RENDER},
      );
    },
    [elementRef],
  );
}

function useScroll(
  containerRef: RefObject<HTMLElement>,
  maxScrollTop: number,
  getNextScrollTop: (scrollTop: number) => number,
  customOnScroll?: HandlerOf<VirtualScrollerScrollContext>,
): [number, EventHandler] {
  const requestAnimationFrame = useAnimationFrame(containerRef);
  const forceUpdate = useForceUpdate();

  // The scroll offset from an unprocessed scroll event.
  const eventScrollTopRef = useRef<number>();
  const eventScrollTop = eventScrollTopRef.current;
  eventScrollTopRef.current = undefined;

  // The scroll offset from an unprocessed scroll request.
  const requestedScrollTopRef = useRef<number>();

  // The scroll offset before taking into account a new scroll request.
  const rawNextScrollTop = findFirstScrollTop(containerRef, eventScrollTop, requestedScrollTopRef.current);
  // Compensate for elastic scrolling in Safari.
  const nextScrollTop = clamp(rawNextScrollTop, 0, maxScrollTop);

  // Resolve a new scroll request.
  const nextRequestedScrollTop = getNextScrollTop(nextScrollTop);
  if (nextRequestedScrollTop !== nextScrollTop) {
    requestedScrollTopRef.current = nextRequestedScrollTop;
  }

  // The scroll offset after taking into account a new scroll request.
  const rawScrollTop = findFirstScrollTop(containerRef, eventScrollTop, requestedScrollTopRef.current);
  const scrollTop = clamp(rawScrollTop, 0, maxScrollTop);

  // Handle scroll events.
  const onScroll: EventHandler = useCallback(
    (event) => {
      // Getting the scrollTop now is free, getting it later may cost us.
      eventScrollTopRef.current = getScrollTopFromContainer(containerRef);

      // If we should be reporting the context
      if (customOnScroll) {
        customOnScroll(getScrollDetailsFromContainer(containerRef, event));
      }

      requestAnimationFrame(() => {
        forceUpdate();
      });
    },
    [containerRef, requestAnimationFrame, forceUpdate, customOnScroll],
  );

  // Perform scroll requests.
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const requestedScrollTop = requestedScrollTopRef.current;
    if (isUndefined(requestedScrollTop)) {
      return;
    }

    requestedScrollTopRef.current = undefined;
    container.scrollTop = requestedScrollTop;
  });

  return [scrollTop, onScroll];
}

/** Allocates items using memory about previous renders, which optimizes for both scrolling and reordering. */
function useMemoryViewportItemIndexes<TId>(itemIds: ReadonlyArray<TId>) {
  // Keep a history of previous allocations (indexes are reusable components, values are item IDs).
  const prevAllocationsRef = useRef<ReadonlyArray<TId | undefined>>([]);
  const prevAllocations = prevAllocationsRef.current;

  // Allocate the same item IDs to the same reusable components.
  const allocatedItemIndexes = new Set<number>();
  const itemIndexesFromPrevious = times(
    Math.max(prevAllocations.length, itemIds.length),
    (allocationIndex) => {
      // Did this reusable component have an item?
      const prevItemId =
        allocationIndex < prevAllocations.length ? prevAllocations[allocationIndex] : undefined;
      if (isUndefined(prevItemId)) {
        return undefined;
      }

      // Is this item still visible?
      const itemIndex = itemIds.indexOf(prevItemId);
      if (itemIndex === -1) {
        return undefined;
      }

      // Allocate it to the same reusable component!
      allocatedItemIndexes.add(itemIndex);
      return itemIndex;
    },
  );

  // Allocate the remaining items to the empty slots.
  const itemIndexesToAllocate = times(itemIds.length).filter(
    (itemIndex) => !allocatedItemIndexes.has(itemIndex),
  );
  const itemIndexes = itemIndexesFromPrevious.map((allocatedItemIndex) => {
    // Has this already been allocated?
    if (!isUndefined(allocatedItemIndex)) {
      return allocatedItemIndex;
    }

    // Are there more items to allocate?
    const itemIndex = itemIndexesToAllocate.shift();
    if (!isUndefined(itemIndex)) {
      return itemIndex;
    }

    return -1;
  });

  // Trim any unused slots off the end.
  while (itemIndexes[itemIndexes.length - 1] === -1) {
    itemIndexes.pop();
  }

  // Remember the item IDs for next time.
  prevAllocationsRef.current = itemIndexes.map((itemIndex) =>
    itemIndex === -1 ? undefined : itemIds[itemIndex],
  );

  return itemIndexes;
}

interface ItemRenderResult<TId> {
  itemId: TId;
  renderItem: RendererOf<TId>;
  children: ReactNode;
}

/** Provides smart memoization for the content of scroller items. */
function useItemChildren<TId>(
  itemId: TId | undefined,
  renderItem: RendererOf<TId>,
  validateItem: PredicateOf<TId>,
): ReactNode {
  const prevRenderResultRef = useRef<ItemRenderResult<TId>>();
  const prevRenderResult = prevRenderResultRef.current;

  // If this reusable item is unused, use the previous render result to keep the component mounted.
  if (isUndefined(itemId)) {
    return prevRenderResult && validateItem(prevRenderResult.itemId) ? prevRenderResult.children : null;
  }

  // If the render dependencies are equal, use the previous render result.
  if (prevRenderResult && prevRenderResult.itemId === itemId && prevRenderResult.renderItem === renderItem) {
    return prevRenderResult.children;
  }

  const children = renderItem(itemId);
  prevRenderResultRef.current = {
    itemId,
    renderItem,
    children,
  };
  return children;
}

interface SectionHeaderRenderResult {
  sectionIndex: number;
  topOffset: number;
  renderSectionHeader: (...args: any[]) => ReactNode;
  children: ReactNode;
}

/** Provides smart memoization for the content of scroller sections. */
function useSectionHeaderChildren(
  sectionIndex: number,
  topOffset: number,
  renderSectionHeader: (...args: any[]) => ReactNode,
): ReactNode {
  const prevRenderResultRef = useRef<SectionHeaderRenderResult>();
  const prevRenderResult = prevRenderResultRef.current;

  // If the render dependencies are equal, use the previous render result.
  if (
    prevRenderResult &&
    prevRenderResult.sectionIndex === sectionIndex &&
    prevRenderResult.topOffset === topOffset &&
    prevRenderResult.renderSectionHeader === renderSectionHeader
  ) {
    return prevRenderResult.children;
  }

  const children = renderSectionHeader(sectionIndex, topOffset);
  prevRenderResultRef.current = {
    sectionIndex,
    topOffset,
    renderSectionHeader,
    children,
  };
  return children;
}

/*
 * Helpers.
 */

/** Computes the target scroll range, given the viewport size and focus changes. */
function computeVirtualScrollerScrollRange<TId>(
  prevLayout: VirtualScrollerLayout<TId>,
  focusedId: TId | undefined,
  focusedItemTop: number,
  itemHeight: number,
  virtualHeight: number,
  viewportHeight: number,
): [number, number, number] {
  // Compute the possible scroll range given the viewport size, to avoid scrolling to an impossible position.
  const absoluteMinScrollTop = 0;
  const absoluteMaxScrollTop = Math.max(0, virtualHeight - viewportHeight);

  // Determine the previously focused item, whether it was visible, and its virtual position.
  const prevFocusedId = prevLayout.focusedId;
  const [prevVisibleFocusedId, prevFocusedItemTop] = findVisibleFocusedItemInLayout(prevLayout);

  // Determine whether the focused item changed, regardless of whether it was previously visible.
  const shouldFocusNewFocusedItem = !isUndefined(focusedId) && focusedId !== prevFocusedId;

  // Determine whether the focused item was previously visible and changed position.
  const shouldFocusSameFocusedItem =
    !isUndefined(prevVisibleFocusedId) &&
    focusedId === prevVisibleFocusedId &&
    focusedItemTop !== prevFocusedItemTop;

  // If we have to recalculate the scroll position
  if (shouldFocusNewFocusedItem || shouldFocusSameFocusedItem) {
    const focusedItemBottom = focusedItemTop + itemHeight;

    // Compute the scroll range to make the focused item visible.
    // Note: this calculation is tricky in order to cover the case where viewportHeight < itemHeight.
    const focusMinScrollTop = focusedItemBottom - Math.max(viewportHeight, itemHeight);
    const focusMaxScrollTop = focusedItemBottom - Math.min(viewportHeight, itemHeight);

    const minScrollTop = clamp(focusMinScrollTop, absoluteMinScrollTop, absoluteMaxScrollTop);
    const maxScrollTop = clamp(focusMaxScrollTop, absoluteMinScrollTop, absoluteMaxScrollTop);

    return [minScrollTop, maxScrollTop, absoluteMaxScrollTop];
  }

  return [absoluteMinScrollTop, absoluteMaxScrollTop, absoluteMaxScrollTop];
}

/** Finds the ID and position of the focused item in the given layout, if that focused item was visible. */
function findVisibleFocusedItemInLayout<TId>(layout: VirtualScrollerLayout<TId>) {
  return findItem(layout.visibleItemIds, layout.focusedId, layout.firstVisibleItemTop, layout.itemHeight);
}

/** Finds the ID and position of the given item, if it exists. */
function findItem<TId>(
  itemIds: ReadonlyArray<TId>,
  requestedItemId: TId | undefined,
  firstItemTop: number,
  itemHeight: number,
): [TId | undefined, number] {
  const itemIndex = isUndefined(requestedItemId) ? -1 : itemIds.indexOf(requestedItemId);
  const itemId = itemIndex === -1 ? undefined : requestedItemId;
  const itemTop = itemIndex === -1 ? NaN : firstItemTop + itemIndex * itemHeight;

  return [itemId, itemTop];
}

/** Computes the distance by which the layout has shifted due to items being added/removed at the top. */
function computeVirtualScrollerLayoutDelta<TId>(
  prevLayout: VirtualScrollerLayout<TId>,
  itemIds: readonly TId[],
  firstItemTop: number,
  itemHeight: number,
) {
  // Compute how much each of the previously visible items have shifted.
  const itemDeltas = prevLayout.visibleItemIds
    .map((itemId, prevItemIndex) => {
      // Try to find the previously visible item in the new virtual layout.
      const [, itemTop] = findItem(itemIds, itemId, firstItemTop, itemHeight);
      if (Number.isNaN(itemTop)) {
        return undefined;
      }

      // Compute how much this item has shifted.
      const prevItemTop = prevLayout.firstVisibleItemTop + prevItemIndex * prevLayout.itemHeight;
      return itemTop - prevItemTop;
    })
    .filter(isDefined);

  // Find the most common shift among all the previously visible items.
  const maxItemDeltaAndCount = _(itemDeltas)
    .countBy(identity)
    .toPairs()
    .maxBy(([, count]) => count);
  if (!maxItemDeltaAndCount) {
    return 0;
  }

  const [itemDeltaAsString] = maxItemDeltaAndCount;
  return Number(itemDeltaAsString);
}

/** Decides if we should stay at the top, or scroll to compensate for layout shift. */
function computeShouldStayAtTop<TId>(
  prevLayout: VirtualScrollerLayout<TId>,
  firstItemTop: number,
  itemHeight: number,
  viewportHeight: number,
  layoutDelta: number,
  nextScrollTop: number,
): boolean {
  // Are we exactly at the top of the first item (not at the top loader)?
  const isAtTop = nextScrollTop === prevLayout.firstVisibleItemTop;

  // The maximum offset to keep the first item in the viewport.
  const maxLayoutDeltaToStayAtTop = viewportHeight - firstItemTop - itemHeight;

  return isAtTop && layoutDelta > 0 && layoutDelta <= maxLayoutDeltaToStayAtTop;
}

/** Smart clamps the given scroll position to the given range. */
function computeNextScrollTop(
  scrollTop: number,
  minScrollTop: number,
  maxScrollTop: number,
  viewportHeight: number,
): number {
  // If it is already in range, do nothing.
  if (minScrollTop <= scrollTop && scrollTop <= maxScrollTop) {
    return scrollTop;
  }

  // If we have to scroll more than about a screenful, center in the range.
  const distanceToScroll = Math.max(minScrollTop - scrollTop, scrollTop - maxScrollTop);
  if (distanceToScroll > maxScrollTop - minScrollTop && distanceToScroll > viewportHeight * 0.5) {
    return Math.round(minScrollTop * 0.33 + maxScrollTop * 0.67);
  }

  if (scrollTop < minScrollTop) {
    return minScrollTop;
  }

  return maxScrollTop;
}

/** Selects the first available source of scrolling information. */
function findFirstScrollTop(
  containerRef: RefObject<HTMLElement>,
  eventScrollTop: number | undefined,
  requestedScrollTop: number | undefined,
) {
  // If we having a pending scroll request, use that.
  if (!isUndefined(requestedScrollTop)) {
    return requestedScrollTop;
  }

  // If we just had a scroll event, use that.
  if (!isUndefined(eventScrollTop)) {
    return eventScrollTop;
  }

  // Otherwise, use the scrollTop from the container.
  return getScrollTopFromContainer(containerRef);
}

function getScrollTopFromContainer(containerRef: RefObject<HTMLElement>) {
  const container = containerRef.current;
  return container ? Math.max(0, container.scrollTop) : 0;
}

function getScrollDetailsFromContainer(
  containerRef: RefObject<HTMLElement>,
  event: FrontEvent,
): VirtualScrollerScrollContext {
  const container = containerRef.current;

  if (!container) {
    return {
      event,
      scrollTop: 0,
      scrollLeft: 0,
      scrollRemainingSpaceRight: 0,
    };
  }

  const clientWidth = Math.max(0, container.clientWidth);
  const scrollLeft = Math.max(0, container.scrollLeft);
  const scrollWidth = Math.max(0, container.scrollWidth);
  return {
    event,
    scrollTop: Math.max(0, container.scrollTop),
    scrollLeft,
    scrollRemainingSpaceRight: Math.round(scrollWidth - (clientWidth + scrollLeft)),
  };
}

// Round to the nearest device pixel to avoid blurriness in Chrome/Electron.
function roundToNearestDevicePixel(top: number, devicePixelRatio: number): number {
  return Math.round(top * devicePixelRatio) / devicePixelRatio;
}

//
function computeHasLoader(moreStatus: AsyncMoreStatusesEnum): boolean {
  return (
    moreStatus === AsyncMoreStatusesEnum.HAS_MORE ||
    moreStatus === AsyncMoreStatusesEnum.LOADING ||
    moreStatus === AsyncMoreStatusesEnum.ERROR
  );
}
